/**
 * MentionsDropdown — Bandeja de @-menciones al lado de la campana.
 *
 * Similar a NotificationsDropdown pero filtrado a tipos:
 *   - "mention"      (menciones en comentarios de tareas)
 *   - "mencion-chat" (menciones en chat de cliente)
 *
 * Click en item → navega al deeplink del comentario:
 *   /tareas?focus=TASK-XXX&tab=chat&comment=CMT-XXX
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AtSign, CheckCheck } from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { Tooltip } from "./ui/tooltip.jsx";
import { BandejaPanel, PildoraBandeja, FilaBandeja, TituloGrupo, EsqueletoBandeja, VacioBandeja, agruparPorDia, haceCuanto } from "./BandejaPanel.jsx";
import { useAuth } from "../context/AuthContext.jsx";

const POLL_INTERVAL = 20_000;
const MENCION_TIPOS = "mention,mencion-chat";

export function MentionsDropdown() {
  const { miembro } = useAuth();
  const userId = miembro?.id;
  const navigate = useNavigate();
  const [open, setOpen]               = useState(false);
  const [tab, setTab]                 = useState("unread"); // unread | all
  const [mentions, setMentions]       = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading]         = useState(false);
  const wrapRef   = useRef(null);
  const btnRef    = useRef(null);
  const panelRef  = useRef(null);

  const fetchMentions = useCallback(async (currentTab) => {
    if (!userId) { setMentions([]); setUnreadCount(0); return; }
    setLoading(true);
    try {
      const params = { user_id: userId, tipos: MENCION_TIPOS, limit: 30 };
      if ((currentTab || tab) === "unread") params.unread_only = "true";
      const res = await api.getNotifications(params);
      setMentions(res.notifications ?? []);
      if (typeof res.unread_count === "number") setUnreadCount(res.unread_count);
    } catch {
      setMentions([]);
    } finally {
      setLoading(false);
    }
  }, [userId, tab]);

  // Poll + refresh on focus/visibility
  useEffect(() => {
    fetchMentions();
    const id = setInterval(() => {
      if (!document.hidden) fetchMentions();
    }, POLL_INTERVAL);
    const onFocus = () => fetchMentions();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchMentions]);

  // Refetch al cambiar tab
  useEffect(() => {
    if (open) fetchMentions(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Click outside cierra
  useEffect(() => {
    function onClick(e) {
      const inBtn = wrapRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inBtn && !inPanel) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  async function handleClickMention(m) {
    // Marcar leída en background
    if (!m.leida) {
      api.markNotifRead(m.id).catch(() => {});
      setMentions(prev => prev.map(n => n.id === m.id ? { ...n, leida: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    }
    // Navegar al deeplink del link guardado, o derivar de entidad_id
    if (m.link) {
      navigate(m.link);
    } else if (m.entidad_tipo === "tarea" && m.entidad_id) {
      navigate(`/tareas?focus=${m.entidad_id}&tab=chat`);
    }
    setOpen(false);
  }

  async function handleMarkAllRead() {
    if (!userId || unreadCount === 0) return;
    // Marca todas como leídas (incluye notifs no-mention, pero la UX es: vaciás @-inbox)
    // Para ser preciso, marcamos sólo las visibles
    setMentions(prev => prev.map(n => ({ ...n, leida: true })));
    setUnreadCount(0);
    await Promise.all(
      mentions.filter(m => !m.leida).map(m => api.markNotifRead(m.id).catch(() => {}))
    );
    if (tab === "unread") setMentions([]);
  }

  const grupos = agruparPorDia(mentions);
  return (
    <div ref={wrapRef} className="relative">
      <Tooltip content="Menciones" side="bottom">
        <button
          ref={btnRef}
          onClick={() => setOpen(o => !o)}
          className="relative h-[46px] w-[46px] inline-flex items-center justify-center rounded-full bg-card shadow-[0_1px_3px_rgba(17,19,24,0.06)] hover:shadow-md text-muted-foreground hover:text-foreground transition-all"
          aria-label="Bandeja de menciones"
        >
          <AtSign className="h-[22px] w-[22px]" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#7C3AED] text-white text-[12px] font-extrabold flex items-center justify-center ring-2 ring-background">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </Tooltip>

      <BandejaPanel
        abierto={open} onCerrar={() => setOpen(false)} anclaRef={btnRef} panelRef={panelRef}
        titulo="Menciones" subtitulo={unreadCount > 0 ? `${unreadCount} sin leer` : "Todo al día"}
        acciones={tab === "unread" && unreadCount > 0 ? (
          <button onClick={handleMarkAllRead} className="h-10 px-3 rounded-full text-[14px] font-extrabold text-[#5B21B6] flex items-center gap-1 active:bg-muted">
            <CheckCheck className="h-4 w-4" />Marcar todas
          </button>
        ) : null}
        pestanas={<>
          <PildoraBandeja activa={tab === "unread"} onClick={() => setTab("unread")}>Sin leer{unreadCount > 0 ? ` · ${unreadCount}` : ""}</PildoraBandeja>
          <PildoraBandeja activa={tab === "all"} onClick={() => setTab("all")}>Todas</PildoraBandeja>
        </>}
      >
        {loading && mentions.length === 0 && <EsqueletoBandeja />}
        {!loading && mentions.length === 0 && (
          <VacioBandeja icono="alternate_email"
            titulo={tab === "unread" ? "No tenés menciones pendientes" : "Sin menciones todavía"}
            detalle="Cuando alguien escriba @tu-nombre en un comentario aparece acá." />
        )}
        {grupos.map(g => (
          <div key={g.t}>
            <TituloGrupo>{g.t}</TituloGrupo>
            {g.items.map(m => (
              <FilaBandeja key={m.id} icono="alternate_email" tono="v"
                titulo={m.titulo || "Te mencionaron en una tarea"} vista={m.mensaje} hora={haceCuanto(m.created_at)} sinLeer={!m.leida}
                onAbrir={() => handleClickMention(m)}
                onLeida={() => {
                  api.markNotifRead(m.id).catch(() => {});
                  setMentions(prev => tab === "unread" ? prev.filter(n => n.id !== m.id) : prev.map(n => n.id === m.id ? { ...n, leida: true } : n));
                  setUnreadCount(c => Math.max(0, c - 1));
                }} />
            ))}
          </div>
        ))}
      </BandejaPanel>
    </div>
  );
}

